import { auth } from '@mesa/core/auth/better-auth';
import type { Scope } from '@mesa/core/auth/types';
import { scopesForRole } from '@mesa/core/auth/types';
import type { Invitation, Member, Organization } from '@mesa/core/org/index';
import type { UpdateOrgInput } from '@mesa/core/org/mutations';
import { getOrgSlugById, getOrgWithMembership, listExpiredPendingInvitations } from '@mesa/core/org/queries';
import { createServerFn } from '@tanstack/react-start';
import { getRequestHeaders, setResponseHeader } from '@tanstack/react-start/server';
import { baseAuthMiddleware, sessionAuthMiddleware } from '../middleware/base-auth';

function forwardCookies(responseHeaders: Headers) {
  const cookies = responseHeaders.getSetCookie();
  if (cookies.length > 0) {
    setResponseHeader('Set-Cookie', cookies);
  }
}

type OrgInfo = {
  id: string;
  slug: string;
  name: string;
  role: string;
  scopes: Scope[];
};

// Return-shape annotations needed because Better Auth's generic return types
// collapse to `any` through TanStack Start's serialization boundary.
type MemberWithUser = Member & { user: { name: string; email: string; image?: string | null } };

type InvitationDetail = Invitation & {
  organizationName: string;
  organizationSlug: string;
  inviterEmail: string;
};

// ---------------------------------------------------------------------------
// Auth bootstrap functions
// ---------------------------------------------------------------------------
// These bypass baseAuthMiddleware because they ARE the auth resolution layer.
// Route beforeLoad calls these to establish session/org context that
// baseAuthMiddleware itself depends on. Auth validation happens in Better Auth
// via the session cookie.

export const getOrgBySlugServerFn = createServerFn({ method: 'GET' })
  .validator((data: { orgSlug: string }) => data)
  .handler(async ({ data: { orgSlug } }) => {
    const headers = getRequestHeaders();
    const session = await auth.api.getSession({ headers });
    if (!session) throw new Error('Unauthorized');

    const result = await getOrgWithMembership(orgSlug, session.user.id);
    if (!result) return null;

    return {
      id: result.org.id,
      slug: result.org.slug,
      name: result.org.name,
      role: result.member.role,
      scopes: scopesForRole(result.member.role),
    } satisfies OrgInfo;
  });

// Auth bootstrap -- bypasses baseAuthMiddleware for the same reason as getSession/getOrgBySlugServerFn
// above: it's called from route beforeLoad to sync the session cookie when the URL slug
// doesn't match activeOrganizationId. Better Auth validates the session via the cookie.
export const syncActiveOrg = createServerFn({ method: 'POST' })
  .validator((data: { orgId: string }) => data)
  .handler(async ({ data: { orgId } }) => {
    const headers = getRequestHeaders();

    const { headers: responseHeaders, response } = await auth.api.setActiveOrganization({
      headers,
      body: { organizationId: orgId },
      returnHeaders: true,
    });

    if (!response) {
      throw new Error('syncActiveOrg: no response from setActiveOrganization');
    }

    forwardCookies(responseHeaders);

    return response;
  });

// ---------------------------------------------------------------------------
// Session-only server functions (no org context)
// ---------------------------------------------------------------------------
// These use sessionAuthMiddleware because they operate outside org context
// (the user may not belong to any org yet, or is acting on a personal
// invitation).

export const createOrgServerFn = createServerFn({ method: 'POST' })
  .middleware([sessionAuthMiddleware])
  .validator((data: { name: string; slug: string }) => data)
  .handler(async ({ data, context: { session } }) => {
    const org = await auth.api.createOrganization({
      body: { name: data.name, slug: data.slug, userId: session.user.id },
    });

    // Set the new org as active so the cookie reflects it immediately
    const headers = getRequestHeaders();
    const { headers: responseHeaders } = await auth.api.setActiveOrganization({
      headers,
      body: { organizationId: org.id },
      returnHeaders: true,
    });
    forwardCookies(responseHeaders);

    return org;
  });

export const listOrgsServerFn = createServerFn({ method: 'GET' })
  .middleware([sessionAuthMiddleware])
  .handler(async (): Promise<Organization[]> => {
    const headers = getRequestHeaders();
    return (await auth.api.listOrganizations({ headers })) as Organization[];
  });

export const getInvitationServerFn = createServerFn({ method: 'GET' })
  .middleware([sessionAuthMiddleware])
  .validator((data: { invitationId: string }) => data)
  .handler(async ({ data, context: { session } }) => {
    const headers = getRequestHeaders();
    const inv = (await auth.api.getInvitation({ headers, query: { id: data.invitationId } })) as InvitationDetail;
    if (!inv) throw new Error('Invitation not found');
    if (inv.email.toLowerCase() !== session.user.email.toLowerCase()) {
      throw new Error('Forbidden');
    }
    const expired = inv.status === 'pending' && inv.expiresAt && new Date(inv.expiresAt) < new Date();
    return { ...inv, expired: !!expired };
  });

export const acceptInvitationServerFn = createServerFn({ method: 'POST' })
  .middleware([sessionAuthMiddleware])
  .validator((data: { invitationId: string }) => data)
  .handler(async ({ data }) => {
    const headers = getRequestHeaders();
    const response = await auth.api.acceptInvitation({
      headers,
      body: { invitationId: data.invitationId },
    });
    const organizationId = response.invitation.organizationId;

    // BA updates the session's active org in the DB but does not emit a fresh
    // Set-Cookie for the response; call setActiveOrganization explicitly so the
    // UI's cookie matches before it navigates to /$orgSlug/*.
    const { headers: responseHeaders } = await auth.api.setActiveOrganization({
      headers,
      body: { organizationId },
      returnHeaders: true,
    });
    forwardCookies(responseHeaders);

    const orgSlug = await getOrgSlugById(organizationId);
    if (!orgSlug) throw new Error('Organization not found after accept');
    return { orgId: organizationId, orgSlug };
  });

export const rejectInvitationServerFn = createServerFn({ method: 'POST' })
  .middleware([sessionAuthMiddleware])
  .validator((data: { invitationId: string }) => data)
  .handler(async ({ data }) => {
    const headers = getRequestHeaders();
    await auth.api.rejectInvitation({ headers, body: { invitationId: data.invitationId } });
  });

// ---------------------------------------------------------------------------
// Middleware-based server functions (org context required)
// ---------------------------------------------------------------------------

export const updateOrgServerFn = createServerFn({ method: 'POST' })
  .middleware([baseAuthMiddleware])
  .validator((data: { orgSlug: string } & UpdateOrgInput) => data)
  .handler(async ({ data: { orgSlug: _orgSlug, ...patch }, context: { org } }) => {
    const headers = getRequestHeaders();
    await auth.api.updateOrganization({
      headers,
      body: { data: patch, organizationId: org.id },
    });
  });

export const deleteOrgServerFn = createServerFn({ method: 'POST' })
  .middleware([baseAuthMiddleware])
  .validator((data: { orgSlug: string }) => data)
  .handler(async ({ data: _data, context: { org } }) => {
    const headers = getRequestHeaders();
    await auth.api.deleteOrganization({
      headers,
      body: { organizationId: org.id },
    });
  });

export const listMembersServerFn = createServerFn({ method: 'GET' })
  .middleware([baseAuthMiddleware])
  .validator((data: { orgSlug: string }) => data)
  .handler(async ({ data: _data, context: { org } }): Promise<MemberWithUser[]> => {
    const headers = getRequestHeaders();
    const result = await auth.api.listMembers({ headers, query: { organizationId: org.id } });
    return result.members as MemberWithUser[];
  });

export const removeMemberServerFn = createServerFn({ method: 'POST' })
  .middleware([baseAuthMiddleware])
  .validator((data: { orgSlug: string; memberId: string }) => data)
  .handler(async ({ data, context: { org } }) => {
    const headers = getRequestHeaders();
    await auth.api.removeMember({
      headers,
      body: { memberIdOrEmail: data.memberId, organizationId: org.id },
    });
  });

export const listInvitationsServerFn = createServerFn({ method: 'GET' })
  .middleware([baseAuthMiddleware])
  .validator((data: { orgSlug: string }) => data)
  .handler(async ({ data: _data, context: { org } }): Promise<Invitation[]> => {
    const headers = getRequestHeaders();
    return (await auth.api.listInvitations({ headers, query: { organizationId: org.id } })) as Invitation[];
  });

// BA's `findPendingInvitation` filters out expired rows, so resending on an expired invite leaves
// the old row behind as a phantom pending entry. Cancel those ourselves before handing off to BA.
async function cancelExpiredPendingInvitations(headers: Headers, orgId: string, email: string): Promise<void> {
  const expired = await listExpiredPendingInvitations(orgId, email);
  await Promise.all(expired.map((inv) => auth.api.cancelInvitation({ headers, body: { invitationId: inv.id } })));
}

export const inviteMemberServerFn = createServerFn({ method: 'POST' })
  .middleware([baseAuthMiddleware])
  .validator((data: { orgSlug: string; email: string; role: 'owner' | 'admin' | 'member' }) => data)
  .handler(async ({ data, context: { org } }) => {
    const headers = getRequestHeaders();
    await cancelExpiredPendingInvitations(headers, org.id, data.email);
    return auth.api.createInvitation({
      headers,
      body: {
        email: data.email,
        role: data.role,
        organizationId: org.id,
        resend: true,
      },
    });
  });

export const cancelInvitationServerFn = createServerFn({ method: 'POST' })
  .middleware([sessionAuthMiddleware])
  .validator((data: { invitationId: string }) => data)
  .handler(async ({ data }) => {
    const headers = getRequestHeaders();
    await auth.api.cancelInvitation({ headers, body: { invitationId: data.invitationId } });
  });
